import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileSearch, RefreshCw, FileSpreadsheet, Loader2, AlertTriangle,
  ShieldAlert, Package,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Feb 2026 — ASN Release-Hold Audit (READ-ONLY, super-admin only).
 *
 * Lists every ASN released from hold since `since`, pairs any
 * pre-fix duplicate ledger rows, compares declared vs scanned vs
 * booked totals, and proposes a correction PER PRODUCT. A second
 * table lists every SKU touched by the audit with its current
 * Dashboard / pallet / batch / corrected totals.
 *
 * Writes nothing — the backend endpoint and this UI are strictly
 * read-only. The CSV download uses the logged-in admin bearer so
 * the file is scoped to the super-admin's session (just like the
 * Day Close XLSX export).
 */

const DEFAULT_SINCE = '2026-10-05';

export default function AdminReleaseHoldAudit() {
  const token = localStorage.getItem('mr_admin_token') || '';
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [since, setSince] = useState(DEFAULT_SINCE);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(
        `${API}/admin/release-hold-audit`,
        { headers: authHeaders, params: { since } },
      );
      setReport(data);
      setForbidden(false);
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) {
        setForbidden(true);
        setReport(null);
      } else {
        const detail = err.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Failed to load audit.');
        setReport(null);
      }
    } finally {
      setLoading(false);
    }
  }, [authHeaders, since]);

  useEffect(() => {
    load();
  }, [load]);

  const downloadCsv = useCallback(async () => {
    setDownloading(true);
    try {
      const res = await axios.get(
        `${API}/admin/release-hold-audit.csv`,
        { headers: authHeaders, params: { since }, responseType: 'blob' },
      );
      const filename = `asn-release-hold-audit-${since}.csv`;
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'CSV download failed.');
    } finally {
      setDownloading(false);
    }
  }, [authHeaders, since]);

  // ------------- Forbidden state (sub-admin hit the page) -------------
  if (forbidden) {
    return (
      <AdminLayout>
        <div className="max-w-xl mx-auto mt-24 bg-rose-50 border border-rose-200 rounded-xl p-8 text-center" data-testid="release-hold-audit-forbidden">
          <ShieldAlert className="w-10 h-10 text-rose-600 mx-auto mb-3" />
          <div className="text-lg font-semibold text-rose-900">Super admin only</div>
          <div className="text-sm text-rose-700 mt-2">
            The ASN Release-Hold Audit is restricted to super administrators.
            This page is read-only and never writes to the database.
          </div>
        </div>
      </AdminLayout>
    );
  }

  const perAsn = report?.per_asn || [];
  const perSku = report?.per_sku || [];

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="release-hold-audit-page">
        {/* ---------- Header ---------- */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <FileSearch className="w-6 h-6 text-indigo-600" />
              <h1 className="text-2xl font-semibold text-neutral-900">
                ASN Release-Hold Audit
              </h1>
              <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-indigo-100 text-indigo-700">
                Read-only
              </span>
            </div>
            <div className="text-sm text-neutral-600 mt-1">
              Lists every ASN released from hold since the start date below.
              Pre-fix duplicate ledger pairs are flagged with a proposed
              correction. This page writes nothing.
            </div>
          </div>
          <div className="flex items-end gap-2">
            <label className="text-xs text-neutral-700 flex flex-col gap-1" data-testid="release-hold-audit-since-wrap">
              <span className="font-medium">Start date</span>
              <input
                type="date"
                value={since}
                onChange={(e) => setSince(e.target.value || DEFAULT_SINCE)}
                className="px-2 py-1.5 border border-neutral-300 rounded text-sm"
                data-testid="release-hold-audit-since-input"
              />
            </label>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="px-3 py-1.5 rounded-md border border-neutral-300 bg-white text-sm hover:bg-neutral-50 flex items-center gap-1.5 disabled:opacity-50"
              data-testid="release-hold-audit-refresh"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Refresh
            </button>
            <button
              type="button"
              onClick={downloadCsv}
              disabled={downloading || loading || !report}
              className="px-3 py-1.5 rounded-md bg-emerald-600 text-white text-sm hover:bg-emerald-700 flex items-center gap-1.5 disabled:opacity-50"
              data-testid="release-hold-audit-csv"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
              Download CSV
            </button>
          </div>
        </div>

        {/* ---------- Start-date banner ---------- */}
        <div className="rounded-md bg-indigo-50 border border-indigo-100 px-3 py-2 text-xs text-indigo-900" data-testid="release-hold-audit-since-banner">
          <span className="font-semibold">Audit range:</span>{' '}
          every release since <span className="font-mono">{since}</span>.
          Default start date is <span className="font-mono">{DEFAULT_SINCE}</span>.
        </div>

        {error && (
          <div className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-800 flex items-center gap-2" data-testid="release-hold-audit-error">
            <AlertTriangle className="w-4 h-4" />
            {error}
          </div>
        )}

        {/* ---------- Summary chips ---------- */}
        {report && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="release-hold-audit-summary">
            <SummaryChip label="Generated" value={fmtTs(report.generated_at)} />
            <SummaryChip label="ASNs" value={report.asn_count} />
            <SummaryChip label="SKUs" value={report.sku_count} />
            <SummaryChip
              label="Duplicate pairs"
              value={report.duplicate_pair_count}
              intent={report.duplicate_pair_count > 0 ? 'rose' : 'emerald'}
            />
          </div>
        )}

        {/* ---------- Section 1 — Per-ASN × Product ---------- */}
        <section className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 bg-neutral-50 border-b border-neutral-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-neutral-500" />
              <h2 className="text-sm font-semibold text-neutral-900">
                Per ASN × Product
              </h2>
            </div>
            <span className="text-[11px] text-neutral-500">
              {perAsn.length} ASN{perAsn.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs" data-testid="release-hold-audit-asn-table">
              <thead className="bg-neutral-50 text-neutral-600">
                <tr>
                  <Th>ASN #</Th>
                  <Th>Released</Th>
                  <Th>SKU</Th>
                  <Th>Product</Th>
                  <Th right>Declared</Th>
                  <Th right>Scanned</Th>
                  <Th right>Ledger rows</Th>
                  <Th right>Booked</Th>
                  <Th right>Dup. pairs</Th>
                  <Th right>Correction (Δ)</Th>
                  <Th>Note</Th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><Td colSpan={11} center>Loading…</Td></tr>
                )}
                {!loading && perAsn.length === 0 && (
                  <tr><Td colSpan={11} center>No releases found since {since}.</Td></tr>
                )}
                {!loading && perAsn.flatMap((asn) =>
                  (asn.products || []).length === 0
                    ? [(
                      <tr key={asn.asn_number} data-testid={`release-hold-audit-asn-row-${asn.asn_number}`}>
                        <Td mono>{asn.asn_number}</Td>
                        <Td>{fmtTs(asn.released_at)}</Td>
                        <Td colSpan={9} italic>No product rows</Td>
                      </tr>
                    )]
                    : asn.products.map((p, idx) => (
                      <tr
                        key={`${asn.asn_number}-${p.product_id}`}
                        className={p.proposed_correction_qty !== 0 ? 'bg-rose-50/40' : ''}
                        data-testid={`release-hold-audit-asn-row-${asn.asn_number}-${p.product_id}`}
                      >
                        {idx === 0 ? (
                          <>
                            <Td mono rowSpan={asn.products.length}>{asn.asn_number}</Td>
                            <Td rowSpan={asn.products.length}>{fmtTs(asn.released_at) || '—'}</Td>
                          </>
                        ) : null}
                        <Td mono>{p.sku || '—'}</Td>
                        <Td truncate>{p.name || '—'}</Td>
                        <Td right mono>{fmtInt(p.declared_qty)}</Td>
                        <Td right mono emphasis>{fmtInt(p.scanned_qty)}</Td>
                        <Td right mono>{p.ledger_entries?.length ?? 0}</Td>
                        <Td right mono>{fmtInt(p.ledger_booked_total)}</Td>
                        <Td right mono intent={p.duplicate_pair_count > 0 ? 'rose' : null}>
                          {p.duplicate_pair_count}
                        </Td>
                        <Td right mono intent={p.proposed_correction_qty !== 0 ? 'rose' : 'emerald'}>
                          {p.proposed_correction_qty > 0 ? '+' : ''}{fmtInt(p.proposed_correction_qty)}
                        </Td>
                        <Td small>{p.proposed_correction_note}</Td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* ---------- Section 2 — Per SKU totals ---------- */}
        <section className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 bg-neutral-50 border-b border-neutral-200 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-neutral-900">
              Per SKU — Dashboard vs Pallet vs Batch vs Corrected
            </h2>
            <span className="text-[11px] text-neutral-500">
              {perSku.length} SKU{perSku.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs" data-testid="release-hold-audit-sku-table">
              <thead className="bg-neutral-50 text-neutral-600">
                <tr>
                  <Th>SKU</Th>
                  <Th>Product</Th>
                  <Th right>Dashboard</Th>
                  <Th right>Pallet</Th>
                  <Th right>Batch</Th>
                  <Th right>Corrected</Th>
                  <Th right>Dash − Pallet</Th>
                  <Th right>Batch − Pallet</Th>
                  <Th right>Dup. booked</Th>
                  <Th center>Needs fix?</Th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><Td colSpan={10} center>Loading…</Td></tr>
                )}
                {!loading && perSku.length === 0 && (
                  <tr><Td colSpan={10} center>No SKUs in audit.</Td></tr>
                )}
                {!loading && perSku.map((s) => (
                  <tr
                    key={s.product_id}
                    className={s.needs_correction ? 'bg-amber-50/50' : ''}
                    data-testid={`release-hold-audit-sku-row-${s.product_id}`}
                  >
                    <Td mono>{s.sku || '—'}</Td>
                    <Td truncate>{s.name || '—'}</Td>
                    <Td right mono>{fmtInt(s.dashboard_qty)}</Td>
                    <Td right mono>{fmtInt(s.pallet_qty)}</Td>
                    <Td right mono>{fmtInt(s.batch_qty)}</Td>
                    <Td right mono emphasis>{fmtInt(s.corrected_qty)}</Td>
                    <Td right mono intent={s.dashboard_minus_pallet !== 0 ? 'rose' : null}>
                      {s.dashboard_minus_pallet > 0 ? '+' : ''}{fmtInt(s.dashboard_minus_pallet)}
                    </Td>
                    <Td right mono intent={s.batch_minus_pallet !== 0 ? 'rose' : null}>
                      {s.batch_minus_pallet > 0 ? '+' : ''}{fmtInt(s.batch_minus_pallet)}
                    </Td>
                    <Td right mono>{fmtInt(s.duplicate_booked_qty)}</Td>
                    <Td center>
                      {s.needs_correction ? (
                        <span className="px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-semibold">Yes</span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold">No</span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <div className="text-[11px] text-neutral-400 text-right">
          This report is strictly read-only. The backend endpoints
          <code className="mx-1">/api/admin/release-hold-audit</code> and
          <code className="mx-1">.csv</code> never mutate stock, ledger,
          batch or placement data.
        </div>
      </div>
    </AdminLayout>
  );
}

// ---------------------------------- helpers ----------------------------------

function SummaryChip({ label, value, intent }) {
  const palette = {
    rose: 'bg-rose-50 border-rose-200 text-rose-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  }[intent] || 'bg-white border-neutral-200 text-neutral-800';
  return (
    <div className={`rounded-md border px-3 py-2 ${palette}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-base font-semibold mt-0.5">{value ?? '—'}</div>
    </div>
  );
}

function Th({ children, right, center }) {
  const align = right ? 'text-right' : center ? 'text-center' : 'text-left';
  return (
    <th className={`px-3 py-2 ${align} font-medium border-b border-neutral-200 whitespace-nowrap`}>
      {children}
    </th>
  );
}

function Td({ children, right, center, colSpan, rowSpan, mono, truncate, italic, emphasis, intent, small }) {
  const palette = {
    rose: 'text-rose-700 font-semibold',
    emerald: 'text-emerald-700 font-semibold',
  }[intent] || '';
  const align = right ? 'text-right' : center ? 'text-center' : 'text-left';
  const cls = [
    'px-3 py-1.5 border-b border-neutral-100',
    align,
    mono ? 'font-mono' : '',
    truncate ? 'max-w-[280px] truncate' : '',
    italic ? 'italic text-neutral-500' : '',
    emphasis ? 'font-semibold text-neutral-900' : '',
    small ? 'text-[11px] text-neutral-600' : '',
    palette,
  ].filter(Boolean).join(' ');
  return (
    <td className={cls} colSpan={colSpan} rowSpan={rowSpan}>
      {children}
    </td>
  );
}

function fmtInt(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString();
}

function fmtTs(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return String(ts);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return String(ts);
  }
}
